// Kwartieren waarin de laadpoort (revenue_max) een door de DP geplande
// laadbeurt weigerde. Tot deze wijziging stond zo'n kwartier in de prognose
// groen ("Laden") en na passage oranje ("Eigen verbruik"), zonder uitleg.
// Nu: grijze diagonale arcering met label "geweigerd" en de reden in de
// tooltip. Ontwerp door Bram goedgekeurd (mockup inkoop-geweigerd, deel a:
// verleden en huidig kwartier, gemeten uit shadow_commands.blocked_by/reason).
import { STRATEGIES, type StrategyKey } from './strategy';
import { rgba } from './chartConfig';

export const GEWEIGERD_COLOR = '#5a5a5a';
// Deel b: toekomstige laadslots die de poort volgens het huidige plan zal
// weigeren (voorspelling uit /api/soc-forecast). Lichtgrijze vulling met
// gestippelde grijze rand, zodat gemeten en voorspeld van elkaar te
// onderscheiden zijn.
export const GEWEIGERD_VERWACHT_FILL = 'rgba(160,160,160,0.18)';
export const GEWEIGERD_VERWACHT_RAND = '#8a8a8a';

let geweigerdPattern: { image: HTMLCanvasElement; repeat: 'repeat' } | null | undefined;

// Zelfde tegel als in de mockup: lichtgrijze vulling met één diagonaal.
// Valt terug op null zonder 2D-context (happy-dom); de aanroeper kiest dan
// een vlakke grijze tint.
export function geweigerdHatchFill(): { image: HTMLCanvasElement; repeat: 'repeat' } | null {
  if (geweigerdPattern !== undefined) return geweigerdPattern;
  geweigerdPattern = null;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 8; c.height = 8;
  try {
    const ctx = c.getContext?.('2d');
    if (!ctx) return null;
    ctx.fillStyle = 'rgba(140,140,140,0.25)';
    ctx.fillRect(0, 0, 8, 8);
    ctx.strokeStyle = 'rgba(90,90,90,0.7)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, 8); ctx.lineTo(8, 0);
    ctx.moveTo(-2, 2); ctx.lineTo(2, -2);
    ctx.moveTo(6, 10); ctx.lineTo(10, 6);
    ctx.stroke();
  } catch {
    return null;
  }
  geweigerdPattern = { image: c, repeat: 'repeat' };
  return geweigerdPattern;
}

function euro(v: number): string {
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return `${sign}€${Math.abs(v).toFixed(3).replace('.', ',')}`;
}

/**
 * Korte uitleg voor de tooltip. Haalt marge en eis uit de reason van de
 * laadpoort ("DP plant charge maar geblokkeerd: c_profit=+€0.0250/kWh onder
 * marge-eis €0.0500/kWh, ...") en valt terug op de blocked_by-code.
 */
export function geweigerdTekst(
  blockedBy: string | null | undefined,
  reason: string | null | undefined,
  verwacht = false,
): string {
  const kop = verwacht ? 'Laden verwacht geweigerd door laadpoort' : 'Laden geweigerd door laadpoort';
  const staart = verwacht ? ' (voorspelling)' : '';
  const r = reason ?? '';
  const code = blockedBy ?? '';
  if (code === 'soc_max') return `${kop}: accu op maximum-SOC${staart}`;
  const mProfit = /c_profit=\+?€(-?\d+(?:\.\d+)?)\/kWh/.exec(r);
  const mEis = /marge-eis €(-?\d+(?:\.\d+)?)\/kWh/.exec(r);
  if (mProfit && mEis) {
    return `${kop}: marge ${euro(parseFloat(mProfit[1]))}/kWh onder eis ${euro(parseFloat(mEis[1])).replace('+', '')}/kWh${staart}`;
  }
  if (mProfit) {
    return `${kop}: marge ${euro(parseFloat(mProfit[1]))}/kWh, niet rendabel${staart}`;
  }
  return code ? `${kop} (${code})${staart}` : `${kop}${staart}`;
}

export interface StrategyBandLike {
  startIdx: number;
  endIdx: number;
  strategy: StrategyKey;
  blockedBy?: string | null;
  predictedBlockedBy?: string | null;
}

/** markArea-data voor de strategiebanden; geweigerde banden gearceerd. */
export function strategyBandMarkArea(bands: StrategyBandLike[]) {
  return bands.map(b => {
    if (!b.blockedBy && b.predictedBlockedBy) {
      return [
        {
          xAxis: b.startIdx,
          itemStyle: {
            color: GEWEIGERD_VERWACHT_FILL,
            borderColor: GEWEIGERD_VERWACHT_RAND,
            borderWidth: 1,
            borderType: 'dashed' as const,
          },
          label: {
            show: b.endIdx - b.startIdx >= 2, position: 'insideTop' as const,
            formatter: 'verwacht geweigerd', color: '#555', fontSize: 9, fontWeight: 600,
          },
        },
        { xAxis: b.endIdx },
      ];
    }
    if (!b.blockedBy) {
      return [
        { xAxis: b.startIdx, itemStyle: { color: rgba(STRATEGIES[b.strategy].color, 0.35) } },
        { xAxis: b.endIdx },
      ];
    }
    const fill = geweigerdHatchFill();
    return [
      {
        xAxis: b.startIdx,
        itemStyle: { color: fill ?? rgba(GEWEIGERD_COLOR, 0.25) },
        // Label vanaf twee kwartierbuckets; een smallere band leest via
        // arcering, legenda en tooltip.
        label: {
          show: b.endIdx - b.startIdx >= 2, position: 'insideTop' as const,
          formatter: 'geweigerd', color: '#444', fontSize: 9, fontWeight: 600,
        },
      },
      { xAxis: b.endIdx },
    ];
  });
}
